import React from 'react';
import {
  LayoutRouteContent,
  PathRouteComponent,
  PathRouteContent,
} from './routeNode';

type BoundaryContentFn = () => LayoutRouteContent;

/**
 * Composes multiple boundary components into a single boundary.
 *
 * Each boundary is a function that returns a Promise of a component that
 * wraps its children (or shows an unauthorized page).
 *
 * The boundaries are nested in order: the first boundary wraps the second,
 * which wraps the third, etc.
 *
 * Example:
 * ```ts
 * content: composeBoundaries([
 *   paramResourcePermissionBoundary(unauthorizedAccess, 'workspace/admin', ':workspaceId'),
 *   featureFlagBoundary([Features.SURVEYS]),
 * ]),
 * ```
 */
export const composeBoundaries =
  (boundaries: BoundaryContentFn[]) => async (): PathRouteContent => {
    const components = await Promise.all(boundaries.map(fn => fn()));

    const ComposedBoundary: PathRouteComponent = ({ children }) =>
      components.reduceRight<React.ReactNode>(
        (acc, Component) => React.createElement(Component, null, acc),
        children
      );

    return ComposedBoundary;
  };
